import type { BotTemplate } from "src/services/platformapi";

/**
 * Companion characters come from lovelica-core: seeded safe bots
 * (`AvatarTemplate`, `isNsfw: false`, ids `tl-<name>`) with flat card fields
 * (`greeting`, `tagline`, `accentColor`, …) and the art theme as the one
 * entry in `tags`. `fromTemplate` checks them once, so a bot without them is
 * skipped (and logged) instead of rendering half a template.
 */

/** Labels for the themes the content uses; an unknown slug is title-cased. */
const THEME_LABELS: Record<string, string> = {
  realistic: "Real life",
  "3d": "Clay",
  fantasy: "Fantasy",
  rpg: "Adventure",
};

export function themeLabel(theme: string): string {
  return (
    THEME_LABELS[theme] ??
    theme.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export type Companion = {
  /** The bot template id, e.g. "tl-sarah". */
  id: string;
  name: string;
  gender: "female" | "male" | null;
  age: number | null;
  tagline: string;
  bio: string;
  traits: string[];
  interests: string[];
  avatarURL: string | null;
  greeting: string;
  /** A slug from the content (realistic, 3d, fantasy, rpg, …). */
  theme: string;
  /** Signature colour, `#rrggbb`, for card and chat accents. */
  color: string;
};

const isString = (v: unknown): v is string =>
  typeof v === "string" && v.trim() !== "";
const stringList = (v: readonly string[]): string[] => v.filter(isString);

/** A bot template → a Companion, or null when it lacks the card fields. */
export function fromTemplate(template: BotTemplate): Companion | null {
  const theme = template.tags?.[0];
  const color = template.accentColor;
  if (
    !isString(template.greeting) ||
    !isString(template.tagline) ||
    !isString(theme) ||
    typeof color !== "string" ||
    !/^#[0-9a-fA-F]{6}$/.test(color)
  ) {
    console.error(`Companion ${template.id}: missing card fields`);
    return null;
  }
  const picture = template.profilePicture;
  return {
    id: template.id,
    name: template.name,
    gender:
      template.gender === "FEMALE" ? "female" : template.gender === "MALE" ? "male" : null,
    age: template.age ?? null,
    tagline: template.tagline,
    bio: template.description ?? "",
    traits: stringList(template.personalityTraits),
    interests: stringList(template.interests),
    avatarURL: picture?.urls?.large ?? picture?.downloadUrl ?? null,
    greeting: template.greeting,
    theme,
    color,
  };
}
